/**
 * Flowchart step editor.
 *
 * Editing a node is a **plan patch**, not a display change: the form writes the underlying
 * `WorkflowStep` fields and the caller re-projects the graph. Structural nodes (branch, loop,
 * sub-template) only accept a label/retry ceiling here — changing their shape is a builder-chat
 * proposal, where the validator can see the whole graph.
 */

import { useState } from "react";
import { describeStep, patchWorkflowStep, type StepEdit } from "../lib/planPatches";
import type { FlowchartHint, WorkflowPlan, WorkflowStep } from "../lib/types";

interface StepEditorProps {
  plan: WorkflowPlan;
  flowchart: FlowchartHint[];
  /** Read-only while another device holds the draft lease. */
  disabled?: boolean;
  onApply: (plan: WorkflowPlan, flowchart: FlowchartHint[]) => void;
}

const STRUCTURAL = new Set(["branch", "loop", "sub_template"]);

export function StepEditor({ plan, flowchart, disabled = false, onApply }: StepEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [testIds, setTestIds] = useState("");
  const [output, setOutput] = useState("");
  const [source, setSource] = useState("");
  const [artifactName, setArtifactName] = useState("");
  const [reason, setReason] = useState("");
  const [auth, setAuth] = useState(true);
  const [action, setAction] = useState("");
  const [maxAttempts, setMaxAttempts] = useState("2");
  const [error, setError] = useState<string | null>(null);

  const begin = (step: WorkflowStep) => {
    setEditingId(step.step_id);
    setError(null);
    setLabel(flowchart.find((hint) => hint.step_id === step.step_id)?.label ?? "");
    setUrl(step.url ?? "");
    setTestIds((step.test_ids ?? []).join(", "));
    setOutput(step.captures?.[0]?.output ?? "");
    setSource(step.captures?.[0]?.source ?? "");
    setArtifactName(step.artifact_name ?? "");
    setReason(step.reason ?? "");
    setAuth(step.auth ?? true);
    setAction(step.action ?? "");
    setMaxAttempts(String(step.max_attempts ?? 2));
  };

  const step = plan.steps.find((candidate) => candidate.step_id === editingId);

  const apply = () => {
    if (!step) return;
    const edit: StepEdit = {
      label,
      maxAttempts: Number(maxAttempts),
    };
    if (step.kind === "navigate") edit.url = url;
    else if (step.kind === "verify") edit.testIds = testIds.split(",");
    else if (step.kind === "extract") {
      edit.output = output;
      edit.source = source;
    } else if (step.kind === "download") edit.artifactName = artifactName;
    else if (step.kind === "hitl_request") {
      edit.reason = reason;
      edit.auth = auth;
    } else if (step.kind === "form_submit") edit.action = action;

    const result = patchWorkflowStep(plan, flowchart, step.step_id, edit);
    if (result.error) {
      setError(result.error);
      return;
    }
    onApply(result.plan, result.flowchart);
    setEditingId(null);
  };

  return (
    <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="serif-heading text-lg">Steps</h2>
        <span className="text-xs text-[var(--tj-muted)]">
          Editing a step is a plan patch; the flowchart is re-projected from it.
        </span>
      </div>
      <ol className="mt-2 divide-y divide-[var(--tj-border)]">
        {plan.steps.map((step) => (
          <li key={step.step_id} className="py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-medium">{describeStep(step, flowchart)}</p>
                <p className="font-mono text-xs text-[var(--tj-muted)]">
                  {step.kind} · retry {step.max_attempts ?? 2}
                  {step.url ? ` · ${step.url}` : ""}
                </p>
              </div>
              <button
                type="button"
                className="tj-btn tj-btn-secondary"
                data-testid={`step-edit-${step.step_id}`}
                disabled={disabled}
                onClick={() => (editingId === step.step_id ? setEditingId(null) : begin(step))}
              >
                {editingId === step.step_id ? "Close" : "Edit"}
              </button>
            </div>

            {editingId === step.step_id ? (
              <form
                className="mt-2 space-y-2 rounded-lg border border-[var(--tj-border)] p-2"
                data-testid="step-editor"
                onSubmit={(event) => {
                  event.preventDefault();
                  apply();
                }}
              >
                {error ? (
                  <p className="tj-badge tj-badge-red block rounded p-2 text-xs" role="alert">
                    {error}
                  </p>
                ) : null}
                <label className="block text-xs font-semibold">
                  Label (display hint)
                  <input
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                    className="mt-1 w-full rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-normal"
                  />
                </label>

                {step.kind === "navigate" ? (
                  <label className="block text-xs font-semibold">
                    URL
                    <input
                      value={url}
                      onChange={(event) => setUrl(event.target.value)}
                      className="mt-1 w-full rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-mono font-normal"
                      data-testid="step-url"
                    />
                  </label>
                ) : null}

                {step.kind === "verify" ? (
                  <label className="block text-xs font-semibold">
                    Verification test ids (comma separated)
                    <input
                      value={testIds}
                      onChange={(event) => setTestIds(event.target.value)}
                      className="mt-1 w-full rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-mono font-normal"
                    />
                  </label>
                ) : null}

                {step.kind === "extract" ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="block text-xs font-semibold">
                      Output name
                      <input
                        value={output}
                        onChange={(event) => setOutput(event.target.value)}
                        className="mt-1 w-full rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-mono font-normal"
                      />
                    </label>
                    <label className="block text-xs font-semibold">
                      Source
                      <input
                        value={source}
                        onChange={(event) => setSource(event.target.value)}
                        className="mt-1 w-full rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-mono font-normal"
                      />
                    </label>
                  </div>
                ) : null}

                {step.kind === "download" ? (
                  <label className="block text-xs font-semibold">
                    Artifact name
                    <input
                      value={artifactName}
                      onChange={(event) => setArtifactName(event.target.value)}
                      className="mt-1 w-full rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-mono font-normal"
                    />
                  </label>
                ) : null}

                {step.kind === "hitl_request" ? (
                  <>
                    <label className="block text-xs font-semibold">
                      Reason
                      <input
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        className="mt-1 w-full rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-normal"
                      />
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={auth}
                        onChange={(event) => setAuth(event.target.checked)}
                      />
                      Login/2FA wall (can run in the pre-run auth phase)
                    </label>
                  </>
                ) : null}

                {step.kind === "form_submit" ? (
                  <label className="block text-xs font-semibold">
                    Form action (optional)
                    <input
                      value={action}
                      onChange={(event) => setAction(event.target.value)}
                      className="mt-1 w-full rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-mono font-normal"
                    />
                  </label>
                ) : null}

                {STRUCTURAL.has(step.kind) ? (
                  <p className="rounded border border-dashed border-[var(--tj-border)] p-2 text-xs text-[var(--tj-muted)]">
                    {step.kind} nodes change graph shape; edit them through the builder chat so the
                    validator sees the whole plan.
                  </p>
                ) : null}

                <label className="block text-xs font-semibold">
                  Max attempts
                  <input
                    type="number"
                    min={1}
                    value={maxAttempts}
                    onChange={(event) => setMaxAttempts(event.target.value)}
                    className="mt-1 w-24 rounded border border-[var(--tj-border)] bg-[var(--tj-bg)] px-2 py-1 font-normal"
                  />
                </label>

                <div className="flex gap-2">
                  <button type="submit" className="tj-btn tj-btn-primary">
                    Apply patch
                  </button>
                  <button
                    type="button"
                    className="tj-btn tj-btn-secondary"
                    onClick={() => setEditingId(null)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}
